import {NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {HomeComponent} from './components/home/home.component';
import {PlanDetailComponent} from './components/plan-detail/plan-detail.component';
import {OfficeComponent} from './components/office/office.component';
import {DetailCardComponent} from './components/plan-detail/detail-card/detail-card.component';
import {HeaderComponent} from './components/header/header.component';
import {FooterComponent} from './components/footer/footer.component';
import {LegalTextComponent} from './components/legal-text/legal-text.component';
import {HeroComponent} from './components/hero/hero.component';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {SubheaderComponent} from './components/header/subheader/subheader.component';
import {FaqComponent} from './components/faq/faq.component';
import {SupportComponent} from './components/support/support.component';
import {FaqItemComponent} from './components/common/faq-item/faq-item.component';
import {InviteAFriendComponent} from './components/modals/invite-a-friend/invite-a-friend.component';
import {CardItemComponent} from './components/common/card-item/card-item.component';
import {HeaderTextComponent} from './components/common/header-text/header-text.component';
import {EventsManager} from './events/events.manager';
import {PlansService} from './services/plans.service';
import {FeatureFlagService} from './services/feature.service';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {PhonePipe} from './pipes/phone.pipe';
import {RecaptchaFormsModule, RecaptchaModule} from 'ng-recaptcha-2';
import {environment} from 'src/environments/environment';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {PlanCardComponent} from './components/office/plan-card/plan-card.component';
import {DataService} from './services/data.service';
import {CardContainerComponent} from './components/common/card-container/card-container.component';
import {FeeScheduleComponent} from './components/fee-schedule/fee-schedule.component';
import {NgxMaskDirective, provideEnvironmentNgxMask, NgxMaskConfig} from 'ngx-mask';
import {SessionInterceptor} from './services/interceptors/session.interceptor';
import {PortalCodeInterceptor} from './services/interceptors/portal-code.interceptor';
import {DatePipe} from '@angular/common';
import {LoadingComponent} from './components/common/loading/loading.component';
import {LoadingInterceptor} from './services/interceptors/loading.interceptor';
import {ObjToKeysPipe} from './pipes/objToKeys';
import {ErrorBannerComponent} from './components/common/error-banner/error-banner.component';
import {RangePipe} from './pipes/range';
import {PrivacyPolicyComponent} from './components/privacy-policy/privacy-policy.component';
import {TermsOfUseComponent} from './components/terms-of-use/terms-of-use.component';
import {AppInsightsService} from './services/app-insights.service';
import {LoggingInterceptor} from './services/interceptors/logging.interceptor';
import {OrganizationIdInterceptor} from './core/interceptors/organization-id.interceptor';
import {PlanSummaryComponent} from './components/plan-detail/plan-summary/plan-summary.component';
import {PlanBenefitsComponent} from './components/plan-detail/plan-benefits/plan-benefits.component';
import {CommonDentalSavingsComponent} from './components/plan-detail/common-dental-savings/common-dental-savings.component';
import {AdditionalMemberSavingsComponent} from './components/plan-detail/additional-member-savings/additional-member-savings.component';
import {AncillaryBenefitsComponent} from './components/plan-detail/ancillary-benefits/ancillary-benefits.component';
import {ExclusionsComponent} from './components/plan-detail/exclusions/exclusions.component';
import {AncillaryBenefitsDetailComponent} from './components/ancillary-benefits-detail/ancillary-benefits-detail.component';
import {AncillarySearchComponent} from './components/ancillary-search/ancillary-search.component';
import {ensureTrailingSlash} from './utils/url.util';
import {CONTENT_ENVIRONMENT_CONFIG, PRACTICE_PLANS_ENVIRONMENT_CONFIG, SmartSearchModule, PracticeSelectionFormComponent, SUPPORT_INFO_ENVIRONMENT_CONFIG, PracticePlansService, PlanFooterComponent, COMPILED_FOOTER_FALLBACK_CONFIG, FOOTER_API_ENVIRONMENT_CONFIG, GOOGLE_MAPS_CONFIG} from '@wellfit/wf-plans-services';
import {SMILEHEALTH_PLAN_FOOTER_FALLBACK} from './components/plan-disclosure/plan-footer-fallback.constant';
import {PlanDisclosureComponent} from './components/plan-disclosure/plan-disclosure.component';
import {WfPlansWidgetModule, PaymentMethodEnum} from '@wellfit/wf-plans-widget';
import {PORTAL_CODE} from './app.constants';

const maskConfig: Partial<NgxMaskConfig> = {
  validation: false
};

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    PlanDetailComponent,
    OfficeComponent,
    DetailCardComponent,
    HeaderComponent,
    FooterComponent,
    LegalTextComponent,
    HeroComponent,
    SubheaderComponent,
    FaqComponent,
    SupportComponent,
    FaqItemComponent,
    InviteAFriendComponent,
    CardItemComponent,
    HeaderTextComponent,
    PlanCardComponent,
    PhonePipe,
    ObjToKeysPipe,
    RangePipe,
    CardContainerComponent,
    FeeScheduleComponent,
    LoadingComponent,
    ErrorBannerComponent,
    PrivacyPolicyComponent,
    TermsOfUseComponent,
    ExclusionsComponent,
    AncillaryBenefitsComponent,
    PlanSummaryComponent,
    PlanBenefitsComponent,
    CommonDentalSavingsComponent,
    AdditionalMemberSavingsComponent,
    AncillaryBenefitsDetailComponent,
    AncillarySearchComponent,
    PlanDisclosureComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    NgbModule,
    FormsModule,
    ReactiveFormsModule,
    RecaptchaFormsModule,
    RecaptchaModule,
    BrowserAnimationsModule,
    NgxMaskDirective,
    SmartSearchModule,
    PracticeSelectionFormComponent,
    PlanFooterComponent,
    WfPlansWidgetModule.forRoot({
      environment: environment,
      options: {
        portalCode: PORTAL_CODE,
        branding: 'generic',
        structure: 'generic',
        basePath: '/purchase',
        allowedPaymentMethods: [PaymentMethodEnum.CREDIT_CARD]
      }
    })
  ],
  providers: [
    EventsManager,
    PlansService,
    FeatureFlagService,
    DatePipe,
    {
      provide: CONTENT_ENVIRONMENT_CONFIG,
      useFactory: () => ({
        dataSource: 'api', // Change to 'json' for embedded data
        apiUrl: environment.widgetApi,
        parentDomain: window.location.origin
      })
    },
    {
      provide: PRACTICE_PLANS_ENVIRONMENT_CONFIG,
      useFactory: () => ({
        production: environment.production,
        apiUrl: ensureTrailingSlash(environment.widgetApi) + 'npm/',
        pdfUrl: environment.pdfUrl,
        treatmentApi: environment.treatmentApi
      })
    },
    {
      provide: SUPPORT_INFO_ENVIRONMENT_CONFIG,
      useFactory: () => ({
        apiUrl: environment.widgetApi
      })
    },
    {
      provide: FOOTER_API_ENVIRONMENT_CONFIG,
      useFactory: () => ({
        apiUrl: environment.widgetApi
      })
    },
    {provide: COMPILED_FOOTER_FALLBACK_CONFIG, useValue: SMILEHEALTH_PLAN_FOOTER_FALLBACK},
    // Route the practice zip-code search to the legacy Sales API instead of the
    // plans-widget default. The package's PracticeSelectionFormComponent injects
    // PracticePlansService and only calls getOffices() on it; our PlansService
    // mirrors that interface, so providing it here makes the component use our
    // sales-backed getOffices().
    {provide: PracticePlansService, useClass: PlansService},
    {provide: GOOGLE_MAPS_CONFIG, useValue: {apiKey: environment.googleMapsApiKey}},
    DataService,
    AppInsightsService,
    {provide: HTTP_INTERCEPTORS, useClass: SessionInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: PortalCodeInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: OrganizationIdInterceptor, multi: true},
    provideHttpClient(withInterceptorsFromDi()),
    provideEnvironmentNgxMask(maskConfig)
  ],
  bootstrap: [AppComponent]
})

export class AppModule {}
